// src/CivilianTranslation.jsx
import { useRef, useState } from 'react';
import { getTranslation } from './data/mosTranslations';
import TranslationDraftWorkspace from './TranslationDraftWorkspace';
import { normalizeBranch } from './branch';
import './CivilianTranslation.css';

function PrivacyTooltip() {
  const [visible, setVisible] = useState(false);

  return (
    <div className="ct-tooltip-wrap">
      <button
        type="button"
        className="ct-tooltip-trigger"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
        aria-label="Privacy info"
      >
        ⓘ
      </button>
      {visible && (
        <div className="ct-tooltip-box">
          <button
            type="button"
            className="ct-tooltip-close"
            onClick={() => setVisible(false)}
            aria-label="Close"
          >×</button>
          <p>This is your personal career translation tool. Use it to write your bio, build your resume, and translate your military experience into civilian language. It is never shown to employers, recruiters, or other members.</p>
        </div>
      )}
    </div>
  );
}

export default function CivilianTranslation({ occupation, careerHistory = [], bio = '' }) {
  const translationSectionRef = useRef(null);

  // Nothing selected yet — render nothing
  if (!occupation && careerHistory.length === 0) return null;

  // ── Build display order ───────────────────────────────────────
  // 1. Primary occupation (last held) always first
  // 2. Prior jobs in reverse chron order (most recent prior first)
  const allCodes = [];

  if (occupation) {
    allCodes.push({ ...occupation, isLastHeld: true });
  }

  const sortedPrior = [...careerHistory].sort((a, b) => {
    if (!a.crossTrainedOut && !b.crossTrainedOut) return 0;
    if (!a.crossTrainedOut) return 1;
    if (!b.crossTrainedOut) return -1;
    return Number(b.crossTrainedOut) - Number(a.crossTrainedOut);
  });

  sortedPrior.forEach(entry => {
    if (!allCodes.find(c => c.code === entry.code)) {
      allCodes.push({ ...entry, isLastHeld: false });
    }
  });

  if (allCodes.length === 0) return null;

  // Featured = first item = last held
  const featured = allCodes[0];

  return (
    <div className="ct-wrapper">

      {/* ── Private tool banner ────────────────────────────────── */}
      <div className="ct-private-banner">
        <div className="ct-private-left">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          <span>Only you can see this</span>
          <PrivacyTooltip />
        </div>
      </div>

      {/* ── Card ──────────────────────────────────────────────── */}
      <div className="ct-card" ref={translationSectionRef} tabIndex="-1" aria-label="Civilian Translation profile section">

        {/* Header */}
        {!bio?.trim() && <div className="ct-header">
          <div className="ct-header-left">
            <div className="ct-eyebrow">
              {allCodes.length > 1
                ? 'Civilian Translation — Full Career Picture'
                : 'Civilian Translation'}
            </div>
            <h2 className="ct-title">
              Your service, in civilian terms.
            </h2>
            {allCodes.length > 1 && (
              <p className="ct-header-sub">
                Showing all {allCodes.length} occupation codes from your career history.
                {` Your last position held (${featured.code}) drives your primary job matches.`}
              </p>
            )}
          </div>
          <div className="ct-code-chip">
            <span className="ct-code">{featured.code}</span>
            <span className="ct-code-title">{featured.title}</span>
          </div>
        </div>}

        <TranslationDraftWorkspace bio={bio} occupation={featured} />
        <details className="ct-reference">
          <summary>Explore the general reference for your military specialty</summary>
        {/* ── One block per code ─────────────────────────────── */}
        <div className="ct-blocks">
          {allCodes.map((occ) => {
            const translation = getTranslation(occ.code);
            const isLastHeld  = occ.isLastHeld;

            return (
              <div
                key={occ.code}
                className={`ct-block ${isLastHeld ? 'ct-block-featured' : ''}`}
              >
                {/* Block header */}
                <div className="ct-block-header">
                  <div className="ct-block-code-row">
                    <span className="ct-block-code">{occ.code}</span>
                    <span className="ct-block-code-title">{occ.title}</span>
                    {isLastHeld && (
                      <span className="ct-last-held-badge">Last Position Held</span>
                    )}
                    {!isLastHeld && occ.crossTrainedOut && (
                      <span className="ct-prev-badge">
                        Cross-trained out {occ.crossTrainedOut}
                      </span>
                    )}
                    {!isLastHeld && !occ.crossTrainedOut && (
                      <span className="ct-prev-badge">Previously</span>
                    )}
                  </div>
                  <span className="ct-block-branch">
                    {normalizeBranch(occ.branch)}
                  </span>
                </div>

                {/* Block body */}
                {translation.noData ? (
                  <div className="ct-no-data">
                    <div className="ct-no-data-icon">🔄</div>
                    <div>
                      <p className="ct-no-data-title">
                        Translation coming soon for {occ.code}
                      </p>
                      <p className="ct-no-data-desc">
                        We're building out translations for every occupation code.
                        Your skills still transfer — add your bio and certifications
                        to help recruiters find you.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="ct-grid">

                    <div className="ct-section">
                      <div className="ct-section-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="7" width="20" height="14" rx="2"/>
                          <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                        </svg>
                        Civilian Job Titles
                      </div>
                      <div className="ct-tags">
                        {translation.civilianTitles.map(title => (
                          <span key={title} className="ct-tag ct-tag-title">{title}</span>
                        ))}
                      </div>
                    </div>

                    <div className="ct-section">
                      <div className="ct-section-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                        Transferable Skills
                      </div>
                      <div className="ct-tags">
                        {translation.skills.map(skill => (
                          <span key={skill} className="ct-tag ct-tag-skill">{skill}</span>
                        ))}
                      </div>
                    </div>

                    <div className="ct-section ct-section-full">
                      <div className="ct-section-label">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <circle cx="12" cy="12" r="6"/>
                          <circle cx="12" cy="12" r="2"/>
                        </svg>
                        Target Industries
                      </div>
                      <div className="ct-industries">
                        {translation.industries.map((industry, i) => (
                          <div key={industry} className="ct-industry">
                            <span className="ct-industry-rank">{i + 1}</span>
                            <span className="ct-industry-name">{industry}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="ct-footer-note">
          These translations are a starting point. Your additional duties, leadership roles,
          and certifications add significant value beyond what any code captures.
        </div>

        </details>
      </div>

    </div>
  );
}
